import { SafeUrl } from '@angular/platform-browser';
import { HomeConfiguration } from 'src/app/models/home-configuration';
import { IPCom } from 'src/app/models/ipcom';
import { isBlob } from '../http';
import { Company as CompanyType } from './company';
import { Data, Route as NgRoute } from '@angular/router';

export type ErrorResponse = { success: false; error: string };
export type ExErrorResponse = ErrorResponse & { error_code: number };
export type BasicSuccessResponse = { success: true };
export type WithLang = { lang?: string };
export type WithWarning = { warning: string };

type FlatFormData = [string, string | Blob][];
type BasicFormDataValues = string | number | bigint | boolean | Blob;
type RecursiveFormDataSolverValue = BasicFormDataValues | { [key: string]: RecursiveFormDataSolverValue } | RecursiveFormDataSolverValue[];

const prefixError = (): never => {
  throw new Error('Can\'t have nameless top values');
};

const recursiveFormDataSolver = (value: RecursiveFormDataSolverValue, prefix?: string): FlatFormData => {
  const name = () => prefix ?? prefixError();
  switch (typeof value) {
    case 'string':
      return [[name(), value]];
    case 'bigint':
    case 'number':
    case 'boolean':
      return [[name(), value.toString()]];
    case 'object':
      if (Array.isArray(value)) {
        return value.flatMap((v, i) => recursiveFormDataSolver(v, `${prefix ?? ''}[{${i}}]`));
      }
      if (isBlob(value)) {
        return [[name(), value]];
      }
      if ( !value ) {
        return [[name(), '']]; // null|undefined => ''
      }
      return Object.entries(value).flatMap(([n, v]) => recursiveFormDataSolver(v, `${prefix ? prefix + '.' : ''}${n}`));
    default:
      return value;
  }
};

export const objectToFormData = <TValues extends RecursiveFormDataSolverValue, TObject extends { [key: string]: TValues }>(data: TObject): FormData => {
  const fd = new FormData();
  recursiveFormDataSolver(data, '').forEach(([n, v]) => fd.append(n, v));
  return fd;
};

// Convert js object to query params compatible with php arrays
export const objectToParams = <TValues extends RecursiveFormDataSolverValue, TObject extends { [key: string]: TValues }>(data: TObject): URLSearchParams => {
  const params = new URLSearchParams();
  if (data) {
    recursiveFormDataSolver(data, '')
      .filter(([, v]) => !isBlob(v))
      .forEach(([n, v]) => params.append(n, v as string));
  }
  return params;
};

//@TODO UserData ir TUser ateity reikia suderinti. Ši struktūra naudojama duomenims iš DB, o TUser struktūroje yra atlikta keletas duomenų modifikacijų.
export type CurrentUserData = {
  id: number;
  name: string;
  email: string;
  socket_token: string;
  socket_port: number;
  token: string;
  phone: string;
  date_format: number;
  time_format: string;
  is_social_account: boolean;
  isPasswordSet: boolean;
  homeConfigurations: HomeConfiguration[];
  active: number;
  country: string;
  appVersion: {
    major: number;
    minor: number;
    build: number;
    date: number;
  };
  ownedCompanies: CompanyType[];
  belongsToCompany: CompanyType;
  logo: string | SafeUrl;
  settings: {
    textual: {
      field: string;
      value: string;
    }[];
    togglable: {
      name: string;
      field: string;
      value: boolean;
    }[];
  };
  permissions: PermissionRule;
  lastSeenReactions?: {
    reaction_id: number;
    unseen_events: number;
    last_event_id: number;
  }[];
  reactions?: ReactionData[];
  historySystems?: HistorySystem[];
  panic_settings?: PanicSettings;
  limits: {
    default_system_count: boolean;
    system_count: number;
    default_camera_count: boolean;
    camera_count: number;
  };
  ipcoms?: IPCom[];
  regions?: Region[];
  company_id: number;
  logoDataForStorage?: string;
  access_permission_id: number;
  permission_rules: PermissionRule[];
  companies?: CompanyType[];
};

export type ReactionBasicData = {
  id: number;
  name: string;
  iconPath: string;
  iconPathPng: string;
  companyId: number;
  group: string;
  defaultName: boolean;
};

export type ReactionData = {
  id: number;
  name: string;
  active: boolean;
  company_id: number;
  custom_sound: number;
  default_name: boolean;
  group_id: number;
  image: string;
  image_png: string;
  monas_id: number;
  redirect_to: number;
};

export type ReactionGroupData = {
  id: number;
  company_id: number;
  name: string;
  icon: string;
  default_name: boolean;
};

export type SystemEventData = {
  id: number;
  title: string;
  subTitle: string;
  time: number;
  area: number;
  reaction: number;
  area_event: boolean;
  event_id: number;
  user_id: number;
  user_name: string;
  tags: Tag[];
};

type SystemEventSettingsData = {
  eventTypes?: [{ name: 'All types'; company: -1 }];
  events: SystemEventData[];
  dailyEvents?: [];
  filters?: {
    date_from: null;
    date_to: null;
    area: null;
    system: null;
    type: null;
    company: -1;
    returnEventCount: true;
    forApi: true;
    offsetEvents: 0;
    offsetCount: 0;
    userName: '';
    reactions: null;
  };
};

type SystemThemeData = {
  background_start: string;
  background_end: string;
  full_background: string;
};

type SystemNotificationData = {
  success: true;
  sounds_on: boolean;
  notifications: {
    id: number;
    monas_id: number;
    name: string;
    on: boolean;
    alert_sound_on: boolean;
  }[];
  global_value: boolean;
};

export type SystemSensorData = {
  id: number;
  name: string;
  system_id: string;
  queue_no: number;
  enabled: number; // 0 ir 1?
  type: number;
  alarm_high: boolean;
  alarm_low: boolean;
  high_value: number;
  low_value: number;
};

export type SensorDataWithValues = {
  id: number;
  name: string;
  system_id: number;
  queue_no: number;
  enabled: 1 | 0;
  type: number;
  alarm_high: boolean;
  alarm_low: boolean;
  high_value: number;
  low_value: number;
  status: number; // temperatūra
  warn: boolean;
};

export type SensorIconData = {
  id: number;
  icon: string;
  name: string;
  unit: string;
  name_keyword?: string;
  unit_keyword?: string;
};

export type SystemZoneData = {
  id: number;
  name: string;
  area_id: number;
  queue_no: number;
  system_id: number;
  visible: boolean;
  native: boolean;
};

export type SystemCameraData = {
  id: number;
  name: string;
  full_url: string;
  host: string;
  port: string;
  user: string;
  pass: string;
  path: string;
  system_id: number;
  assigned_pgms: number[];
};

type SystemOwnerData = {
  id: number;
  name: string;
  email: string;
  phone_number: null; // String?
  pivot: { system_id: number; user_id: number }; // Laravel artifaktas
};

// eslint-disable-next-line no-shadow
export enum IoType {
  Disabled = 0,
  Input = 1,
  Output = 2,
}

export type SystemPgmData = {
  id: number;
  name: string;
  icon_number: number;
  queue_no: number;
  enabled: 0 | 1;
  on: 0 | 1;
  pgm_as_area_ck: 0 | 1;
  type: 'level' | 'pulse';
  pulse_time_in_seconds: number;
  pulse_start_time: string | null;
  schedule_no: number;
  io_type: IoType;
  io_ability: '' | 'I' | 'O' | 'M' | 'W';
  area_to_arm: null; // TODO: Better type
};

export type SystemAreaData = {
  id: number;
  system_id: number;
  name: string;
  queue_no: number;
  monas_id: null;
  status: number; // TODO: Better type
  alarm_in_memory: boolean;
  status_time: number;
  last_person_name: string;
  alarm_time: number;
  alarm_type: number; // TODO: Better type
  show_keypad: boolean;
  can_bypass: boolean;
  zones: SystemZoneData[];
};

export type SystemThermostatData = {
  id: number;
  name: string;
  queue_no: number;
  system_id: number;
  temperatures: string; // JSON number[]
  assigned_sensors: string; // JSON number[]
  assigned_output: number;
  active_sensor: number;
  action: number;
};

export type SystemDeviceUserData = {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  key: string;
  queue_no: number;
  system_code_no?: number;
  area_id: number;
  keyboard_code?: string;
  pin?: string;
  protegus_user_id: number;
  system_id: number;
  areas: string;
  can_see_events: boolean;
  canEditUsers: boolean;
  enable_data?: number;
  pgms?: number;
  present?: boolean;
  schedule_no?: number;
  user_id?: number;
};

export type ProtegusUserData = {
  id: number;
  name: string;
  email: string;
  active: 0 | 1;
  created_at: string;
  phone_number: string | null;
  company: string | null;
  country: string | null;
  dashboard_order: string;
  privacy_policy_accepted: boolean;
  soc_account: boolean;
  social_id: string;
  soc_type: string;
  company_id: number;
  access_permission_id: number;
  phone: string | null;
  device_setup_templates: { id: number; name: string }[];
  systems?: { id: number; name: string }[];
};

export type UserSystemData = {
  id: number;
  master: boolean;
  system_permissions_id: number;
  personal_permissions: PermissionList | null;
};

export type SystemProtegusUserData = ProtegusUserData & UserSystemData;

export type SystemData = {
  id: number;
  name: string;
  hwType: string;
  supported_commands: string;
  address: string;
  amIMaster: boolean;
  online: boolean;
  canEditUsers: boolean;
  imei: string;
  mpass: string;
  timeZone: string;
  pgms: SystemPgmData[];
  events: SystemEventSettingsData;
  direct: number;
  areas: SystemAreaData[];
  noSleepStay: boolean;
  hasRealSensors: boolean;
  centralPanel: number;
  coordinates: string;
  theme: SystemThemeData;
  notifications: SystemNotificationData | null;
  sensors: SystemSensorData[];
  signalLevel: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9; // 10?
  zones: SystemZoneData[];
  canBypassZone: boolean;
  canUnbypassZone: boolean;
  protegus_users: SystemProtegusUserData[];
  device_users: SystemDeviceUserData[];
  maxDeviceUsers: number;
  eventConfiguration: string;
  cameras: SystemCameraData[];
  thermostats: SystemThermostatData[];
  installer_id: 0 | number;
  installerEmail: string;
  installerName: string;
  company_id: 0 | number;
  logo: string;
  supportsFireReset: boolean;
  amIWorking: boolean; // False?
  canISeeEvents: boolean; // False?
  device_id: number; // 59 per tris sistemas?
  privacyOfOwners: object[]; // TODO: better type
  owners?: SystemOwnerData[];
  created_at: number;
  related_permissions: PermissionRule[];
  tags: Tag[];
  companyName?: string;
  assistedById?: number;
  assistedByEmail?: string;
};

export type WithLastSystem = {
  lastSystem: SystemData | null;
};

export type Permission = {
  create: boolean;
  view: boolean;
  edit: boolean;
  delete: boolean;
  execute: boolean;
};

export type PermissionList = {
  company_settings: Permission;
  ipcom_settings: Permission;
  reactions: Permission;
  roles: Permission;
  systems: Permission;
  events: Permission;
  tags: Permission;
  users: Permission;
  support_tickets: Permission;
  global_settings: Permission;
  panic_settings: Permission;
  regions: Permission;
  impersonation: Permission;
  transfer_company: Permission;
  transfer_device: Permission;
  sys_advanced_settings: Permission;
  sys_information: Permission;
  sys_areas: Permission;
  sys_zones: Permission;
  sys_outputs: Permission;
  sys_sensors: Permission;
  sys_thermostats: Permission;
  sys_users: Permission;
  sys_receiver: Permission;
  sys_synchronisation: Permission;
  sys_transfer: Permission;
  sys_device_transfer: Permission;
  sys_reset_sensors: Permission;
  sys_assistance: Permission;
  sys_personal_permissions: Permission;
  sys_transfer_to_company: Permission;
  sys_notifications: Permission;
  sys_cameras: Permission;
  sys_events: Permission;
  system: Permission;
  dev_setup_templates: Permission;
};

export enum PermissionRole {
  SuperAdmin = 1,
  Company = 2,
  Installer = 3,
  GeneralUser = 4,
  SystemMaster = 5,
  SystemUser = 6,
};

export type PermissionRule = {
  id: number;
  name: string;
  description: string;
  parent_id: number | null;
  permissions: PermissionList;
  company_id: number;
  role: PermissionRole;
  default: boolean;
  assigned_users?: number;
  tags: Tag[];
};

export type HistorySystem = {
  id: number;
  installer_id: number;
  name: string;
  address: string;
  system_imei: string;
  primary_installer: number;
};

export type Region = {
  id: number;
  name: string;
  api_host: string;
  api_path: string;
  region_version: number;
  api_host_to_use?: string;
};

export type PanicSettings = {
  id: number;
  company_id: number;
  grg_host: string;
  grg_port: number;
  grg_code: string;
  grg_zone: string;
  grg_enabled: boolean;
};

export type Chip = {
  name: string;
  label: string;
  value: boolean;
  modifiableColumn: boolean;
};

export type Tag = {
  id: number;
  name: string;
  company_id: number;
  color: string;
  textColor?: string;
  description: string;
  assigned_systems?: number;
};

export interface RouteData extends Data {
  /**
   * Animacija tarp langų. Nenurodžius bus naudojamas "fade" efektas.
   * Jeigu einama iš lango su mažesne animacijos reikšme į langą su didesne reikšme - naujas langas 'įvažiuos' iš dešinės.
   * Jeigu einama iš lango su didesne animacijos reikšme į langą su mažesne reikšme - naujas langas 'įvažiuos' iš kairės.
   */
  animation?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /**
   * Papildomi duomenys reikalingi PermissionGuard veikimui. Nurodo kokią taisyklę reikia tikrinti.
   */
  rule?: keyof PermissionList;
  /**
   * Papildomi duomenys reikalingi PermissionGuard veikimui.
   * Nurodo kokį prieigos lygį reikia tikrinti. Naudojama kartu su "rule" reikšme.
   * Nenurodžius - naudojama 'view' reikšmė.
   */
  accessLevel?: keyof Permission;
  /**
   * Nišinis parametras. Naudojamas tik tam, kad HomeComponent žinotų, jog reikia atlikti redirect į
   * vartotojo Home be subpath.
   */
  bare?: boolean;
}

export type Routes = Route[];
export interface Route extends NgRoute {
  data?: RouteData;
  children?: Routes;
}
